import { useState, useCallback } from 'react';
import { toLocalYMD } from '../../../utils/dateUtils';
import { HuntSignOutService } from '../services/huntSignOutService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

/**
 * Hook for managing hunt sign-out functionality
 */
export function useHuntSignOut() {
  const { currentUser } = useCurrentUser();
  const [signedOutLocations, setSignedOutLocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Update feature states on the map based on sign-out status
   */
  const updateFeatureStates = useCallback((map, features) => {
    if (!map) return;

    console.log('=== UPDATE FEATURE STATES DEBUG ===');
    console.log('signedOutLocations:', signedOutLocations);
    console.log('features count:', features.length);

    features.forEach(feature => {
      console.log('Processing feature:', feature.id, feature.name);
      
      // Check if this is a point feature (either by geojson or geometry)
      const isPoint = feature.geojson?.type === 'Point' || feature.geometry?.type === 'Point';
      
      if (isPoint) {
        const isOccupied = signedOutLocations[feature.id];
        console.log('Point feature:', feature.id, 'isOccupied:', isOccupied);
        
        try {
          map.setFeatureState(
            { source: 'features-points', id: feature.id },
            { occupied: isOccupied }
          );
          console.log('✅ Set feature state for', feature.id, 'to occupied:', isOccupied);
        } catch (error) {
          console.warn('❌ Failed to set feature state for feature:', feature.id, error);
        }
      }
    });
  }, [signedOutLocations]);

  // (removed unused refreshSignOutStates)

  /**
   * Sign out a hunt location
   */
  const signOutLocation = useCallback(async (locationId, userId, date, timePeriod) => {
    setLoading(true);
    setError(null);
    
    console.log('=== SIGN OUT LOCATION DEBUG ===');
    console.log('locationId:', locationId);
    console.log('userId:', userId);
    console.log('date:', date);
    console.log('timePeriod:', timePeriod);
    
    try {
      await HuntSignOutService.signOutLocation(locationId, userId, date, timePeriod);
      
      // Clear the signed out locations state so it gets re-initialized with fresh data
      setSignedOutLocations({});
      
      return true;
    } catch (error) {
      console.error('Sign out failed:', error);
      setError(error.response?.data?.error || 'Failed to sign out location');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initialize sign-out states from features
   */
  const initializeSignOutStates = useCallback((features) => {
    // Only initialize if the state is empty to avoid overwriting recent changes
    if (Object.keys(signedOutLocations).length > 0) {
      console.log('Skipping initializeSignOutStates - state already has data');
      return;
    }
    
    console.log('Initializing sign-out states from features');
    const today = toLocalYMD(new Date());
    const newSignedOutLocations = {};
    
    features.forEach(feature => {
      if (feature.geojson?.type === 'Point') {
        const isAvailable = HuntSignOutService.isLocationAvailable(feature, today);
        newSignedOutLocations[feature.id] = !isAvailable;
      }
    });
    
    console.log('Initialized sign-out states:', newSignedOutLocations);
    // Avoid triggering loops for maps with no point features
    if (Object.keys(newSignedOutLocations).length === 0) {
      console.log('No point features found; skipping sign-out state initialization');
      return;
    }
    setSignedOutLocations(newSignedOutLocations);
  }, [signedOutLocations]);

  /**
   * Sign in (release) a hunt location after logging or giving up the stand
   * @param {number|string} locationId
   * @param {string} date
   * @param {string} timePeriod
   * @param {{ userId?: number|string|null, requireRemoved?: boolean, refresh?: boolean }} [options]
   */
  const signInLocation = useCallback(async (locationId, date, timePeriod, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      await HuntSignOutService.signInLocation(locationId, date, timePeriod, options);
      setSignedOutLocations({});
      return true;
    } catch (error) {
      console.error('Sign in failed:', error);
      setError(error.response?.data?.error || error.message || 'Failed to clear stand');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check if current user can sign in a location
   */
  const canUserSignIn = useCallback((location, isPresident = false) => {
    if (!currentUser) return false;
    
    const signOutDetails = HuntSignOutService.getTodaySignOutDetails(location);
    if (!signOutDetails) return false;
    
    return parseInt(signOutDetails.user_id) === parseInt(currentUser.id) || isPresident;
  }, [currentUser]);

  return {
    signedOutLocations,
    loading,
    error,
    updateFeatureStates,
    signOutLocation,
    signInLocation,
    initializeSignOutStates,
    canUserSignIn,
    clearError: () => setError(null)
  };
}
